import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2021ed9f"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "toggle-container" }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import GridBase from "../ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { DxToolbarItem } from "devextreme-vue/popup";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import SwitchToggle from "../ui/toggles/switch-toggle.vue";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import NewEditTaskTemplatesModal from "./new-edit-task-templates-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'task-templates-configuration-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * Determines whether the "Select" button should be displayed.
   * If `true`, the button will be visible; otherwise, it will be hidden.
   * @type {boolean}
   * @default false
   */
  showSelectButton: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when a configuration item is selected.
   * This notifies the parent component of the selected item's details.
   * @property {number} id - The unique identifier of the selected configuration item.
   */
  "emitSelectedDetails",
],
  setup(__props, { emit }) {

const props = __props;




const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const userPermissionsStore = useUserPermissionsStore();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const taskTemplatesList = ref<IDDescriptionPair[]>([]);
const showInactiveItems = ref(false);
const userPermissions = ref<UserPermissions>();
const editNewModalId = ref(0);
const selectedConfigurationItem = ref<IDDescriptionPair>({
  id: 0,
  description: "",
});
const displayEditNewModal = ref(false);

const disableEdit = computed(() => {
  return selectedConfigurationItem.value.id == 0;
});

const showInactive = ref(0);

const fetchConfigurationList = async () => {
  try {
    taskTemplatesList.value = await acquaintApiClient.httpGet<
      IDDescriptionPair[]
    >(
      `app/system/lookup/${LookupTableSource.TaskTemplates}?includeinactive=${showInactiveItems.value}`,
    );
  } catch (err) {
    displayErrorMessage("The selected details could not be fetched");
    taskTemplatesList.value = [];
  }
};

watch(
  () => showModal.value,
  async () => {
    if (showModal.value) {
      userPermissions.value = await userPermissionsStore.getUserPermissions();
      fetchConfigurationList();
    }
  },
);

const configurationItemSelected = (e: any) => {
  if (!e) {
    return;
  }
  selectedConfigurationItem.value = e;
};

const configurationItemDoubleClicked = (e: any) => {
  if (!e) {
    return;
  }

  /* Double click needs to work differently depending on the mode the modal is in
   * Select mode needs to emit the clicked value
   * Config mode needs to load the edit modal */
  if (props.showSelectButton) {
    emit("emitSelectedDetails", e.id);
    showModal.value = false;
  } else {
    selectedConfigurationItem.value = e;
    editNewModalId.value = selectedConfigurationItem.value.id;
    displayEditNewModal.value = true;
  }
};

const openEditModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.edit
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to edit these details.",
    );
    return;
  }

  editNewModalId.value = selectedConfigurationItem.value.id;
  displayEditNewModal.value = true;
};

const openNewModal = () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.new
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to create a new system item.",
    );

    return;
  }

  editNewModalId.value = 0;
  displayEditNewModal.value = true;
};

const deleteConfigurationItem = async () => {
  if (
    !userPermissions.value ||
    !userPermissions.value.contactsConfiguration.delete
  ) {
    displayErrorMessage(
      "Sorry, you do not have the permission to delete these details.",
    );
    return;
  }

  if (selectedConfigurationItem.value.id < 1000) {
    displayErrorMessage(
      "Sorry, you can't delete this system item as it is required by Acquaint. However you may edit it, and set it as Inactive to remove it from this list.",
    );
    return;
  }

  const question = await displayQuestionAlert(
    "Are you sure you want to delete these details?",
  );

  if (!question.isConfirmed) {
    return;
  }

  if (await callDeletApi()) {
    selectedConfigurationItem.value = { id: 0, description: "" };
    fetchConfigurationList();
  }
};

const callDeletApi = async () => {
  try {
    await acquaintApiClient.httpDeleteVoid(
      `database/tasktemplates/${selectedConfigurationItem.value.id}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(
      "Sorry, these details cannot be deleted as they are referenced elsewhere.",
    );
    return false;
  }
};

const inactiveToggleValueChange = async () => {
  showInactiveItems.value = showInactive.value ? true : false;
  await fetchConfigurationList();

  if (showInactive.value) {
    return;
  }

  const findInactiveItem = taskTemplatesList.value?.find((item) => {
    return item.id == selectedConfigurationItem.value.id;
  });

  if (!findInactiveItem) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }
};

const newItemCreated = () => {
  selectedConfigurationItem.value = { id: 0, description: "" };
  fetchConfigurationList();
};

const editItem = (inActive: number) => {
  if (inActive && !showInactiveItems.value) {
    selectedConfigurationItem.value = { id: 0, description: "" };
  }

  fetchConfigurationList();
};

const onSelectClick = () => {
  emit("emitSelectedDetails", selectedConfigurationItem.value.id);
  showModal.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "Task/Notes Templates",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      "show-close-button": true,
      height: "460px"
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createVNode(GridBase, {
          dataSource: taskTemplatesList.value,
          onRowClick: configurationItemDoubleClicked,
          onRowSelected: configurationItemSelected,
          "id-column-name": "id",
          showColumnHeaders: false,
          "pre-selected-id": selectedConfigurationItem.value.id
        }, {
          "header-content": _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(SwitchToggle, {
                label: "Show Inactive",
                modelValue: showInactive.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showInactive).value = $event)),
                readOnly: false,
                onOnVaueChange: inactiveToggleValueChange,
                "margin-bottom": "0rem"
              }, null, 8, ["modelValue"])
            ])
          ]),
          columns: _withCtx(() => [
            _createVNode(_unref(DxColumn), {
              "data-field": "description",
              caption: "",
              "hiding-priority": 8,
              width: "100%"
            }),
            _createVNode(_unref(DxColumn), {
              "data-field": "id",
              caption: "Id",
              "hiding-priority": 1
            })
          ]),
          _: 1
        }, 8, ["dataSource", "pre-selected-id"])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Select',
          disabled: disableEdit.value,
          visible: __props.showSelectButton,
          onClick: () => onSelectClick(),
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Delete',
          disabled: disableEdit.value,
          onClick: () => {
            deleteConfigurationItem();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Edit',
          disabled: disableEdit.value,
          onClick: () => {
            openEditModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'New',
          onClick: () => {
            openNewModal();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            showModal.value = false;
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(NewEditTaskTemplatesModal, {
      modelValue: displayEditNewModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayEditNewModal).value = $event)),
      configurationId: editNewModalId.value,
      onConfigurationItemCreated: newItemCreated,
      onConfigurationItemEdited: editItem
    }, null, 8, ["modelValue", "configurationId"])
  ], 64))
}
}

})